import { useState, useMemo } from "react";
import { ELEMENTS as elements } from "../data/periodicTable.js";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";
import { formatNum } from "../lib/formatNum.js";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};
const VALID = new Set(elements.map(e => e.symbol));
const MASS = {};
for (const e of elements) MASS[e.symbol] = typeof e.mass === "number" ? e.mass : parseFloat(e.mass);

// ── exact rational arithmetic (same as EquationBalancer, verified) ──
const gcd = (a,b)=>{a=a<0n?-a:a;b=b<0n?-b:b;while(b){[a,b]=[b,a%b];}return a;};
function rat(n,d=1n){ n=BigInt(n); d=BigInt(d); if(d===0n) throw new Error("div0");
  if(d<0n){n=-n;d=-d;} const g=gcd(n,d)||1n; return [n/g, d/g]; }
const rMul=([a,b],[c,d])=>rat(a*c, b*d);
const rDiv=([a,b],[c,d])=>rat(a*d, b*c);
const rSub=([a,b],[c,d])=>rat(a*d-c*b, b*d);
const rIsZero=([a])=>a===0n;

// ── verified formula parser (identical logic to MolecularWeight, 13/13) ──
function parseFormula(raw){
  if(!raw||!raw.trim())return{counts:null,error:"empty"};
  const f=raw.replace(/[·•∙*]/g,".").trim();
  const segs=[];let buf="";
  for(let i=0;i<f.length;i++){const ch=f[i];
    if(ch==="."&&/[0-9A-Z]/.test(f[i+1]||"")){segs.push(buf);buf="";}else buf+=ch;}
  segs.push(buf);
  // An empty segment means a leading/trailing/doubled hydrate dot
  // (e.g. ".5H2O" or "CuSO4..5H2O") — malformed, not silently skipped.
  if(segs.length>1&&segs.some(x=>!x.trim()))return{counts:null,error:"Malformed formula (stray separator)"};
  const total={};
  for(let seg of segs){seg=seg.trim();if(!seg)continue;
    let mult=1;const m=seg.match(/^(\d+)(?=[A-Z(\[])/);
    if(m){mult=parseInt(m[1]);seg=seg.slice(m[1].length);}
    const r=parseSeg(seg);if(r.error)return{counts:null,error:r.error};
    for(const[el,n]of Object.entries(r.counts))total[el]=(total[el]||0)+n*mult;}
  return{counts:total,error:null};
}
function parseSeg(s){const st=[{}];const bk=[];let i=0;
  while(i<s.length){const ch=s[i];
    if(ch==="("||ch==="["){st.push({});bk.push(ch);i++;continue;}
    if(ch===")"||ch==="]"){i++;let num="";
      while(i<s.length&&/\d/.test(s[i]))num+=s[i++];
      const mu=num?parseInt(num):1;
      if(!bk.length)return{counts:null,error:"Unbalanced parentheses"};
      const open=bk.pop();
      if((open==="("&&ch!==")")||(open==="["&&ch!=="]"))return{counts:null,error:"Mismatched bracket types"};
      const top=st.pop();
      if(!st.length)return{counts:null,error:"Unbalanced parentheses"};
      const into=st[st.length-1];
      for(const[el,n]of Object.entries(top))into[el]=(into[el]||0)+n*mu;continue;}
    const sym=s.slice(i).match(/^[A-Z][a-z]?/);
    if(!sym)return{counts:null,error:`Unexpected character "${ch}"`};
    const el=sym[0];if(!VALID.has(el))return{counts:null,error:`Unknown element "${el}"`};
    i+=el.length;let num="";
    while(i<s.length&&/\d/.test(s[i]))num+=s[i++];
    const c=num?parseInt(num):1;const top=st[st.length-1];
    top[el]=(top[el]||0)+c;}
  if(st.length!==1||bk.length)return{counts:null,error:"Unbalanced parentheses"};
  return{counts:st[0],error:null};
}
function molarMass(f){const{counts,error}=parseFormula(f);if(error||!counts)return null;
  let t=0;for(const[el,n]of Object.entries(counts))t+=MASS[el]*n;return t;}

function balance(eqn){
  const parts=eqn.split(/=|->|→|⟶/);
  if(parts.length!==2) return {error:"Use one '=' or '->' between reactants and products"};
  const [lhsS,rhsS]=parts.map(s=>s.trim());
  if(!lhsS||!rhsS) return {error:"Both sides of the equation are required"};
  const left=lhsS.split("+").map(s=>s.trim()).filter(Boolean);
  const right=rhsS.split("+").map(s=>s.trim()).filter(Boolean);
  if(!left.length||!right.length) return {error:"Need species on both sides"};
  const species=[...left,...right];
  const parsed=[];
  for(const sp of species){
    const {counts,error}=parseFormula(sp);
    if(error) return {error:`"${sp}": ${error}`};
    parsed.push(counts);
  }
  const els=[...new Set(parsed.flatMap(c=>Object.keys(c)))];
  const A=els.map(el=>parsed.map((c,j)=>rat(BigInt((j<left.length?1:-1)*(c[el]||0)))));
  const nS=species.length;
  let row=0; const piv=[];
  for(let col=0; col<nS && row<A.length; col++){
    let sel=-1;
    for(let r=row;r<A.length;r++) if(!rIsZero(A[r][col])){sel=r;break;}
    if(sel<0) continue;
    [A[row],A[sel]]=[A[sel],A[row]];
    const pv=A[row][col];
    A[row]=A[row].map(x=>rDiv(x,pv));
    for(let r=0;r<A.length;r++){ if(r!==row && !rIsZero(A[r][col])){
      const fac=A[r][col];
      A[r]=A[r].map((x,k)=>rSub(x,rMul(fac,A[row][k])));
    }}
    piv.push(col); row++;
  }
  const free=[]; for(let c=0;c<nS;c++) if(!piv.includes(c)) free.push(c);
  if(free.length!==1) return {error: free.length===0 ? "No balancing solution" : "Ambiguous reaction"};
  const fc=free[0];
  const sol=new Array(nS).fill(null).map(()=>rat(0));
  sol[fc]=rat(1);
  for(let i=0;i<piv.length;i++) sol[piv[i]]=rSub(rat(0), A[i][fc]);
  let L=1n; for(const [,d] of sol) L = L/gcd(L,d)*d;
  let ints=sol.map(([n,d])=> n * (L/d));
  let G=0n; for(const v of ints) G=gcd(G, v<0n?-v:v); if(G===0n)G=1n;
  ints=ints.map(v=>v/G);
  if(ints.every(v=>v<=0n)) ints=ints.map(v=>-v);
  if(ints.some(v=>v<=0n)) return {error:"No positive whole-number solution"};
  return { coeffs: ints.map(v=>Number(v)), left, right };
}

const fmt = (x) => formatNum(x, 5);

export default function Stoichiometry() {
  const [eqn, setEqn] = useState("");
  const [amounts, setAmounts] = useState({});   // {species: value}
  const [units, setUnits] = useState({});       // {species: 'g'|'mol'}
  const [product, setProduct] = useState("");

  const balanced = useMemo(() => {
    if (!eqn.trim()) return null;
    try { return balance(eqn); } catch { return { error: "Could not parse equation" }; }
  }, [eqn]);

  const result = useMemo(() => {
    if (!balanced || balanced.error) return null;
    const { coeffs, left, right } = balanced;
    if (!product || !right.includes(product)) return null;
    const rInfo = [];
    for (let i = 0; i < left.length; i++) {
      const sp = left[i];
      const raw = parseFloat(amounts[sp]);
      if (!isFinite(raw) || raw <= 0) return null;
      const mm = molarMass(sp);
      if (!mm) return null;
      const unit = units[sp] || "g";
      const moles = unit === "mol" ? raw : raw / mm;
      rInfo.push({ sp, moles, coeff: coeffs[i], ratio: moles / coeffs[i] });
    }
    if (!rInfo.length) return null;
    let lim = rInfo[0];
    for (const r of rInfo) if (r.ratio < lim.ratio) lim = r;
    const pIdx = right.indexOf(product);
    const pCoeff = coeffs[left.length + pIdx];
    const pMM = molarMass(product);
    const pMoles = lim.ratio * pCoeff;
    return { limiting: lim.sp, theoryMoles: pMoles, theoryGrams: pMoles * pMM, rInfo };
  }, [balanced, amounts, units, product]);

  const inStyle = { width: "100%", padding: "11px 13px", borderRadius: 9, border: `1px solid ${rgba(EM,0.2)}`, background: "rgba(0,0,0,0.3)", color: "#E2E8F0", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box" };

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #FDE9C8)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Stoichiometry</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 600, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 22px" }}>
          Enter a reaction, the amount of each reactant, and pick a product —
          get the limiting reagent and theoretical yield. The equation is
          balanced automatically.
        </p>

        <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Reaction</label>
        <input value={eqn} onChange={(e) => { setEqn(e.target.value); setProduct(""); }} placeholder="e.g. H2 + O2 = H2O" style={{ ...inStyle, marginTop: 6, marginBottom: 16, textAlign: "center", fontSize: 17, fontWeight: 600 }} />

        {balanced?.error && eqn.trim() && (
          <div style={{ padding: "13px 16px", borderRadius: 11, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#FDA4AF", fontSize: 14, textAlign: "center" }}>{balanced.error}</div>
        )}

        {balanced && !balanced.error && (
          <>
            <div style={{ fontSize: 13, color: EM_LIGHT, textAlign: "center", marginBottom: 18 }}>
              Balanced: {balanced.left.map((s,i)=>`${balanced.coeffs[i]!==1?balanced.coeffs[i]:""}${s}`).join(" + ")} → {balanced.right.map((s,i)=>`${balanced.coeffs[balanced.left.length+i]!==1?balanced.coeffs[balanced.left.length+i]:""}${s}`).join(" + ")}
            </div>

            <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Reactant amounts</label>
            <div style={{ display: "grid", gap: 10, marginTop: 8, marginBottom: 18 }}>
              {balanced.left.map((sp) => (
                <div key={sp} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ minWidth: 70, fontSize: 15, fontWeight: 700, color: EM_LIGHT }}>{sp}</span>
                  <input type="number" value={amounts[sp] || ""} onChange={(e) => setAmounts(a => ({ ...a, [sp]: e.target.value }))} placeholder="amount" style={{ ...inStyle, flex: 1 }} />
                  <div style={{ display: "flex", gap: 4 }}>
                    {["g", "mol"].map(u => (
                      <button key={u} onClick={() => setUnits(s => ({ ...s, [sp]: u }))} style={{ padding: "9px 12px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 600, background: (units[sp] || "g") === u ? rgba(EM,0.13) : "rgba(255,255,255,0.02)", border: (units[sp] || "g") === u ? `1.5px solid ${rgba(EM,0.35)}` : "1.5px solid rgba(255,255,255,0.05)", color: (units[sp] || "g") === u ? EM_LIGHT : "#6B7280" }}>{u}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Product to find yield for</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {balanced.right.map(sp => (
                <button key={sp} onClick={() => setProduct(sp)} style={{ padding: "10px 16px", borderRadius: 9, cursor: "pointer", fontFamily: "inherit", fontSize: 14, fontWeight: 600, background: product === sp ? rgba(EM,0.13) : "rgba(255,255,255,0.02)", border: product === sp ? `1.5px solid ${rgba(EM,0.35)}` : "1.5px solid rgba(255,255,255,0.05)", color: product === sp ? EM_LIGHT : "#6B7280" }}>{sp}</button>
              ))}
            </div>
          </>
        )}

        {result && (
          <div style={{ marginTop: 26, display: "grid", gap: 12 }}>
            <div style={{ padding: "16px 20px", borderRadius: 12, background: rgba(EM,0.07), border: `1px solid ${rgba(EM,0.25)}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Limiting reagent</span>
              <span style={{ fontSize: 22, fontWeight: 800, color: EM_LIGHT }}>{result.limiting}</span>
            </div>
            <div style={{ padding: "16px 20px", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Theoretical yield</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: EM_LIGHT }}>{fmt(result.theoryMoles)} mol</span>
            </div>
            <div style={{ padding: "16px 20px", borderRadius: 12, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>= mass of {product}</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: EM_LIGHT }}>{fmt(result.theoryGrams)} g</span>
            </div>
          </div>
        )}
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;} * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
